import React, { useMemo } from "react";

// SVG candlestick chart with Bollinger Bands
export default function CandlestickChart({ candles = [], height = 380 }) {
  const W = 900;
  const H = height;
  const padL = 8;
  const padR = 64;
  const padT = 16;
  const padB = 24;

  const { bars, yToPx, xToPx, bands, gridLines, lastPrice } = useMemo(() => {
    if (!candles.length) return { bars: [], yToPx: () => 0, xToPx: () => 0, bands: null, gridLines: [], lastPrice: null };
    let min = Infinity, max = -Infinity;
    candles.forEach((c) => {
      min = Math.min(min, c.low, c.lower ?? c.low);
      max = Math.max(max, c.high, c.upper ?? c.high);
    });
    const range = max - min || 1;
    min -= range * 0.05;
    max += range * 0.05;
    const plotW = W - padL - padR;
    const plotH = H - padT - padB;
    const n = candles.length;
    const step = plotW / n;
    const bw = Math.max(2, step * 0.6);
    const yToPx = (v) => padT + (1 - (v - min) / (max - min)) * plotH;
    const xToPx = (i) => padL + i * step + step / 2;

    const bars = candles.map((c, i) => ({
      x: xToPx(i),
      bw,
      openY: yToPx(c.open),
      closeY: yToPx(c.close),
      highY: yToPx(c.high),
      lowY: yToPx(c.low),
      up: c.close >= c.open,
    }));

    const linePath = (key) => {
      let d = "";
      candles.forEach((c, i) => {
        if (c[key] == null) return;
        const x = xToPx(i);
        const y = yToPx(c[key]);
        d += d === "" ? `M ${x} ${y}` : ` L ${x} ${y}`;
      });
      return d;
    };
    const areaPath = () => {
      const up = [];
      const lo = [];
      candles.forEach((c, i) => {
        if (c.upper == null || c.lower == null) return;
        up.push(`${xToPx(i)} ${yToPx(c.upper)}`);
        lo.push(`${xToPx(i)} ${yToPx(c.lower)}`);
      });
      if (!up.length) return "";
      return `M ${up.join(" L ")} L ${lo.reverse().join(" L ")} Z`;
    };
    const bands = { upper: linePath("upper"), mid: linePath("mid"), lower: linePath("lower"), area: areaPath() };

    const gridLines = [0, 0.25, 0.5, 0.75, 1].map((f) => {
      const v = min + (max - min) * f;
      return { y: padT + (1 - f) * plotH, label: v.toFixed(v < 10 ? 4 : 2) };
    });

    return { bars, yToPx, xToPx, bands, gridLines, lastPrice: candles[candles.length - 1]?.close };
  }, [candles, H]);

  if (!candles.length) {
    return (
      <div className="flex items-center justify-center h-[380px] text-[#64748B] font-mono text-sm">
        Caricamento grafico…
      </div>
    );
  }

  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="w-full" style={{ height }} preserveAspectRatio="none"
         data-testid="candlestick-svg">
      {gridLines.map((g, i) => (
        <g key={i}>
          <line x1={padL} x2={W - padR} y1={g.y} y2={g.y} stroke="#1A2234" strokeWidth="1" />
          <text x={W - padR + 6} y={g.y + 4} fill="#64748B" fontSize="11" fontFamily="JetBrains Mono">{g.label}</text>
        </g>
      ))}
      {bands?.area && <path d={bands.area} fill="rgba(6,182,212,0.06)" />}
      {bands?.upper && <path d={bands.upper} fill="none" stroke="#00E0B0" strokeWidth="1.2" strokeOpacity="0.7" />}
      {bands?.mid && <path d={bands.mid} fill="none" stroke="#EC1E9C" strokeWidth="1.2" strokeDasharray="4 3" strokeOpacity="0.8" />}
      {bands?.lower && <path d={bands.lower} fill="none" stroke="#00E0B0" strokeWidth="1.2" strokeOpacity="0.7" />}
      {bars.map((b, i) => {
        const color = b.up ? "#00FFC2" : "#EC1E9C";
        const top = Math.min(b.openY, b.closeY);
        const bh = Math.max(1.5, Math.abs(b.closeY - b.openY));
        return (
          <g key={i}>
            <line x1={b.x} x2={b.x} y1={b.highY} y2={b.lowY} stroke={color} strokeWidth="1.2" />
            <rect x={b.x - b.bw / 2} y={top} width={b.bw} height={bh} fill={color} rx="1" />
          </g>
        );
      })}
      {lastPrice != null && (
        <g>
          <line x1={padL} x2={W - padR} y1={yToPx(lastPrice)} y2={yToPx(lastPrice)}
                stroke="#F8FAFC" strokeWidth="0.6" strokeDasharray="2 4" strokeOpacity="0.5" />
          <rect x={W - padR} y={yToPx(lastPrice) - 9} width={padR} height={18} fill="#00FFC2" />
          <text x={W - padR + 6} y={yToPx(lastPrice) + 4} fill="#03110B" fontSize="11" fontWeight="700" fontFamily="JetBrains Mono">
            {lastPrice.toFixed(lastPrice < 10 ? 4 : 2)}
          </text>
        </g>
      )}
    </svg>
  );
}
